import { Modal } from './Modal';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  itemLabel?: string;
  confirmLabel?: string;
  pendingLabel?: string;
  pending?: boolean;
  tone?: 'danger' | 'warning' | 'neutral';
  /** `false` oculta el aviso; las acciones reversibles no deben parecer destructivas. */
  footnote?: string | false;
  /** Mantiene visible una validación del servidor sin cerrar la decisión. */
  error?: string;
  onConfirm: () => void;
  onClose: () => void;
}

/** Confirmación reutilizable; mantiene visible el registro y la consecuencia de la acción. */
export function ConfirmDialog({
  open,
  title,
  description,
  itemLabel,
  confirmLabel = 'Eliminar',
  pendingLabel = 'Eliminando…',
  pending = false,
  tone = 'danger',
  footnote = 'Esta acción no se puede deshacer.',
  error,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const iconTone =
    tone === 'danger'
      ? 'bg-red-50 text-red-600'
      : tone === 'warning'
        ? 'bg-amber-50 text-amber-700'
        : 'bg-blue-50 text-blue-700';
  return (
    <Modal
      open={open}
      title={title}
      protectUnsavedChanges={false}
      preventClose={pending}
      onClose={onClose}
    >
      <div className="space-y-5">
        <div className="flex gap-3">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg font-bold ${iconTone}`}
            aria-hidden
          >
            !
          </span>
          <div>
            <p className="text-sm leading-6 text-slate-600">{description}</p>
            {itemLabel && (
              <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800">
                {itemLabel}
              </p>
            )}
            {error && (
              <p
                role="alert"
                className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm leading-5 text-expense"
              >
                {error}
              </p>
            )}
          </div>
        </div>
        {footnote && <p className="text-xs leading-5 text-slate-500">{footnote}</p>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="button button-secondary w-full sm:w-auto"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className={`button w-full sm:w-auto ${tone === 'danger' ? 'button-danger' : 'button-primary'}`}
          >
            {pending ? pendingLabel : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
